import React from "react";
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { generateFAQSchema } from "../../utils/structuredData";
import styleVariables from "../../variables.module.scss";

const FAQSection: React.FC = () => {
  const faqs = [
    {
      question: "What areas do you serve?",
      answer:
        "We serve Ottawa and all surrounding areas including Kanata, Nepean, Orleans, Barrhaven, and other nearby communities. Contact us to confirm service availability in your specific location.",
    },
    {
      question: "Do you handle electrical and gas work?",
      answer:
        "For electrical work, gas line installations, and other specialized trades requiring licensed professionals, we collaborate with certified contractors who are properly licensed and insured for those specific trades. This ensures all work meets local building codes and safety regulations while providing you with the expertise needed for these critical systems.",
    },
    {
      question: "Are you licensed and insured?",
      answer:
        "Yes, we are fully licensed and insured for general handyman services. For specialized work requiring licensed professionals (such as electrical, major plumbing, gas work, or HVAC), we collaborate with certified contractors who are properly licensed and insured for those specific trades. This ensures all work meets local codes and safety regulations.",
    },
    {
      question: "Do you provide free estimates?",
      answer:
        "Absolutely! We provide free, no-obligation estimates for all our services. We'll assess your project and provide a detailed quote with no hidden fees.",
    },
    {
      question: "How quickly can you start a project?",
      answer:
        "We typically can start most projects within 1-2 weeks, depending on the scope and current schedule. Emergency repairs can often be addressed within 24-48 hours.",
    },
    {
      question: "What types of payment do you accept?",
      answer:
        "We accept cash, credit cards, debit cards, and e-transfers. Payment terms are discussed during the estimate process.",
    },
    {
      question: "Do you offer warranties on your work?",
      answer:
        "Yes, we stand behind all our work with satisfaction guarantees. Specific warranty terms depend on the type of service and materials used.",
    },
    {
      question: "Can you handle both small repairs and large projects?",
      answer:
        "Yes, we handle everything from small repairs like fixing a leaky faucet to larger projects like complete room renovations. No job is too big or too small.",
    },
    {
      question: "Do you work on weekends or evenings?",
      answer:
        "We primarily work Monday through Friday, 8 AM to 6 PM. Weekend work may be available for certain projects - please contact us to discuss your specific needs.",
    },
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <Box sx={{ py: 8, backgroundColor: "white" }}>
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{
            color: styleVariables.themeGreyColor,
            fontWeight: "bold",
            mb: 4,
          }}
        >
          Frequently Asked Questions
        </Typography>
        <Typography
          variant="body1"
          textAlign="center"
          sx={{
            fontSize: "1.1rem",
            lineHeight: 1.7,
            mb: 6,
            maxWidth: "800px",
            mx: "auto",
          }}
        >
          Get answers to common questions about our handyman services in Ottawa.
        </Typography>

        <Box sx={{ maxWidth: "800px", mx: "auto" }}>
          {faqs.map((faq, index) => (
            <Accordion key={index} sx={{ mb: 2, boxShadow: 2 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: styleVariables.themeOrangeColor,
                  "&:hover": {
                    backgroundColor: "#e65100",
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", color: "black" }}
                >
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ backgroundColor: "#f8f9fa" }}>
                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Structured Data for SEO */}
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Container>
    </Box>
  );
};

export default FAQSection;
